import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { 
  Sparkles, 
  Menu, 
  X, 
  Calendar, 
  MessageSquare, 
  LogOut,
  LayoutDashboard,
  Settings,
  Moon,
  Sun,
  Globe,
  Briefcase,
  Shield,
  Lock
} from 'lucide-react';

export const Navbar = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { language, setLanguage, t, languages } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDark, setIsDark] = useState(false);

  const isProvider = user?.role === 'provider';
  const isAdmin = user?.role === 'admin';

  const toggleTheme = () => {
    setIsDark(!isDark);
    document.documentElement.classList.toggle('dark');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isTransparent = location.pathname === '/';

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        isTransparent ? 'bg-transparent' : 'nav-glass'
      }`}
      data-testid="navbar"
    >
      <div className="container-app">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 group"
            data-testid="navbar-logo"
          >
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors ${
              isTransparent ? 'bg-white/20 backdrop-blur-sm' : 'bg-accent'
            }`}>
              <Sparkles className={`w-5 h-5 ${isTransparent ? 'text-white' : 'text-accent-foreground'}`} />
            </div>
            <span className={`font-heading text-2xl font-bold tracking-tight hidden sm:block transition-colors ${
              isTransparent ? 'text-white' : 'text-foreground'
            }`}>
              AFY
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link 
              to="/providers" 
              className={`text-sm font-medium transition-colors hover:text-accent ${
                isTransparent ? 'text-white/90' : 'text-muted-foreground'
              }`}
              data-testid="nav-providers"
            >
              {t('nav.providers')}
            </Link>
            <Link 
              to="/services" 
              className={`text-sm font-medium transition-colors hover:text-accent ${
                isTransparent ? 'text-white/90' : 'text-muted-foreground'
              }`}
              data-testid="nav-services"
            >
              {t('nav.services')}
            </Link>
            
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className={isTransparent ? 'text-white hover:bg-white/10' : ''}
                  data-testid="language-selector"
                >
                  <Globe className="w-4 h-4 mr-1" />
                  <span className="text-lg">{languages[language]?.flag}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {Object.entries(languages).filter(([code]) => ['de', 'en', 'fr'].includes(code)).map(([code, { native, flag }]) => (
                  <DropdownMenuItem
                    key={code}
                    onClick={() => setLanguage(code)}
                    className={language === code ? 'bg-accent/10' : ''}
                    data-testid={`lang-${code}`}
                  >
                    <span className="text-lg mr-2">{flag}</span>
                    {native}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            
            <button 
              onClick={toggleTheme}
              className={`p-2 rounded-full transition-colors ${
                isTransparent ? 'text-white hover:bg-white/10' : 'text-foreground hover:bg-muted'
              }`}
              data-testid="theme-toggle"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="relative h-10 w-10 rounded-full"
                    data-testid="user-menu-trigger"
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback className="bg-accent text-accent-foreground">
                        {user?.name?.charAt(0)?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <div className="flex items-center gap-2 p-2">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback className="bg-accent text-accent-foreground text-xs">
                        {user?.name?.charAt(0)?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col">
                      <p className="text-sm font-medium">{user?.name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to={isAdmin ? '/admin/dashboard' : isProvider ? '/provider/dashboard' : '/dashboard'} className="cursor-pointer" data-testid="menu-dashboard">
                      {isAdmin ? <Shield className="mr-2 h-4 w-4" /> : <LayoutDashboard className="mr-2 h-4 w-4" />}
                      {isAdmin ? 'Administration' : t('nav.dashboard')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/bookings" className="cursor-pointer" data-testid="menu-bookings">
                      <Calendar className="mr-2 h-4 w-4" />
                      {t('nav.bookings')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/messages" className="cursor-pointer" data-testid="menu-messages">
                      <MessageSquare className="mr-2 h-4 w-4" />
                      {t('nav.messages')}
                    </Link>
                  </DropdownMenuItem>
                  {isProvider && (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/provider/blog" className="cursor-pointer" data-testid="menu-provider-blog">
                          <Briefcase className="mr-2 h-4 w-4" />
                          {t('blog.myPosts')}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/provider/profile" className="cursor-pointer" data-testid="menu-provider-profile">
                          <Settings className="mr-2 h-4 w-4" />
                          {t('nav.profile')}
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuItem asChild>
                    <Link to="/change-password" className="cursor-pointer" data-testid="menu-change-password">
                      <Lock className="mr-2 h-4 w-4" />
                      {t('auth.changePassword')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive" data-testid="menu-logout">
                    <LogOut className="mr-2 h-4 w-4" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-3">
                <Button 
                  variant="ghost" 
                  className={isTransparent ? 'text-white hover:bg-white/10' : ''}
                  onClick={() => navigate('/login')}
                  data-testid="nav-login"
                >
                  {t('nav.login')}
                </Button>
                <Button 
                  className="btn-primary"
                  onClick={() => navigate('/register')}
                  data-testid="nav-register"
                >
                  {t('nav.register')}
                </Button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-2 md:hidden">
            {/* Mobile Language */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className={isTransparent ? 'text-white' : ''}
                >
                  <span className="text-lg">{languages[language]?.flag}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {Object.entries(languages).filter(([code]) => ['de', 'en', 'fr'].includes(code)).map(([code, { native, flag }]) => (
                  <DropdownMenuItem
                    key={code}
                    onClick={() => setLanguage(code)}
                    className={language === code ? 'bg-accent/10' : ''}
                  >
                    <span className="text-lg mr-2">{flag}</span>
                    {native}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            
            <button
              className={`p-2 rounded-lg transition-colors ${isTransparent ? 'text-white' : 'text-foreground'}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden glass animate-fade-in">
          <div className="container-app py-4 space-y-4">
            <Link 
              to="/providers" 
              className="block py-2 text-foreground hover:text-accent"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav.providers')}
            </Link>
            <Link 
              to="/services" 
              className="block py-2 text-foreground hover:text-accent"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav.services')}
            </Link>
            
            {isAuthenticated ? (
              <>
                <Link 
                  to={isAdmin ? '/admin/dashboard' : isProvider ? '/provider/dashboard' : '/dashboard'}
                  className="block py-2 text-foreground hover:text-accent"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('nav.dashboard')}
                </Link>
                {isProvider && (
                  <Link 
                    to="/provider/blog"
                    className="block py-2 text-foreground hover:text-accent"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('blog.myPosts')}
                  </Link>
                )}
                <Link 
                  to="/bookings"
                  className="block py-2 text-foreground hover:text-accent"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('nav.bookings')}
                </Link>
                <Link 
                  to="/messages"
                  className="block py-2 text-foreground hover:text-accent"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('nav.messages')}
                </Link>
                <button 
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="block py-2 text-destructive"
                >
                  {t('nav.logout')}
                </button>
              </>
            ) : (
              <div className="flex flex-col gap-2 pt-2">
                <Button 
                  variant="outline"
                  onClick={() => {
                    navigate('/login');
                    setMobileMenuOpen(false);
                  }}
                >
                  {t('nav.login')}
                </Button>
                <Button 
                  className="btn-primary"
                  onClick={() => {
                    navigate('/register');
                    setMobileMenuOpen(false);
                  }}
                >
                  {t('nav.register')}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
